import { useState } from 'react';
import cn from 'classnames';
import { rm } from 'utils';
import { FilterButton } from 'components/Filter/themes/BGH/filterButton';
import { Button } from 'components/Button';
import { filterChanged } from 'views/ProductlistPage/actions';
import { filterToggleReset } from 'components/Filter/actions';
import {
  categoryFilterFacetSelector,
  filterPanelsSelector,
  getFilterPanelsWithoutFacet,
  nbrOfHitsSelector,
  webCatalogSelector
} from 'components/Filter/selector';
import Drawer from 'components/Shared/Drawer';
import { isMobile as isMobileSelector } from 'components/Shared/responsive';
import { Filter as FilterType } from './types';
import { useDispatch, useSelector } from 'react-redux';
import styles from './styles.scss';
import { getCurrencySymbol } from 'store/appShell/selector';
import { getActiveChoices } from 'components/Filter/utils';

interface Props {
  className?: string;
}

function getFilterCaption(hasActiveChoice: boolean, nbrOfHits: number) {
  return hasActiveChoice
    ? nbrOfHits === 1
      ? rm('filter_show_one_product')
      : rm('filter_show_x_products', nbrOfHits)
    : rm('filter_show_all');
}

export const Filter = ({ className }: Props) => {
  const [isOpen, setOpen] = useState(false);
  const open = () => setOpen(true);
  const close = () => setOpen(false);

  const isMobile = useSelector(isMobileSelector);
  const webCatalog = useSelector(webCatalogSelector);
  const nbrOfHits = useSelector(nbrOfHitsSelector);
  const _filterPanels = useSelector(filterPanelsSelector);
  const categoryFilterFacet = useSelector(categoryFilterFacetSelector);
  const filterPanels = getFilterPanelsWithoutFacet(_filterPanels, categoryFilterFacet);
  const currencySymbol = useSelector(getCurrencySymbol);

  const dispatch = useDispatch();
  const onReset = () => {
    dispatch(filterToggleReset());
    void dispatch(filterChanged());
  };

  const activeChoices = getActiveChoices({ filterPanels, currencySymbol });
  const nbrOfActiveChoices = activeChoices.length;
  const hasActiveChoice = nbrOfActiveChoices > 0;

  const filterCaption = getFilterCaption(hasActiveChoice, nbrOfHits);

  return (
    <>
      <FilterButton className={className} onClick={open}>
        <span>{rm('filter_filter_button')}</span>
        {hasActiveChoice && <span className={styles.nbrOfActiveChoices}>{nbrOfActiveChoices}</span>}
      </FilterButton>

      <Drawer
        className={styles.drawer}
        padding="none"
        side="right"
        width={isMobile ? 'full' : 'default'}
        onClose={close}
        transparent={isMobile || undefined}
        isOpen={isOpen}
      >
        <div className={styles.header}>
          <h3>{rm('filter_filter_drawer_header')}</h3>
        </div>

        <div className={styles.body}>
          {filterPanels.map(filter => (
            <FilterType
              filter={filter}
              webCatalog={webCatalog}
              key={`${filter.name}:${webCatalog}:${filter.type}`}
            />
          ))}
        </div>

        <div className={styles.footer}>
          <Button
            disabled={!hasActiveChoice}
            onClick={onReset}
            className={cn(styles.filterButton, styles.clearButton)}
            appearance="tertiary"
          >
            {rm('filter_filter_drawer_clear_button')}
          </Button>
          <Button onClick={close} className={styles.filterButton} appearance="tertiary">
            {filterCaption}
          </Button>
        </div>
      </Drawer>
    </>
  );
};
